/**
 * Drains sealed rows that a Plaid sync couldn't finish updating.
 *
 * The server can't decrypt a sealed row, so when a sync brings changes for one
 * — a pending transaction settling to a different amount, a merchant finally
 * resolving, a location appearing — it can only flag the row
 * (`expenses.PendingClientMerge`) and move on. Nothing is parked server-side:
 * the flag is a bare boolean, and the values are re-fetched live from Plaid
 * when this runs.
 *
 * This is the part that makes that automatic. It fires whenever the client is
 * in a position to do the work — after unlock, and after a sync — so an
 * encrypted account doesn't drift until someone remembers to press a button.
 *
 * It is deliberately quiet: no spinner, no toast, no error surfaced. Nothing
 * here is user-initiated, so a failure just means the flag stays set and the
 * next opportunity tries again. The manual run in Settings is the visible
 * path, for anyone who wants to force it.
 */
import { ref, watch } from 'vue'
import { useEncryption } from '@/composables/useEncryption'
import { useBackfillLocationsMutation, useBankAccountsQuery } from '@/composables/useQueries'
import { useResealMutation } from '@/composables/useReseal'
import type { AuthContext } from '@/utils/api'

/** Shared across callers so two mounted views can't run it twice at once. */
const running = ref(false)
let lastRunAt = 0
/** A sync can fire several cache events in a row; don't chase each one. */
const MIN_INTERVAL_MS = 60_000

export function useSealedMerge(auth: AuthContext) {
  const enc = useEncryption()
  const bankAccountsQuery = useBankAccountsQuery(auth)
  const mergeMutation = useBackfillLocationsMutation(auth)
  const resealMutation = useResealMutation(auth)

  async function drain(reason: string): Promise<void> {
    if (running.value) return
    if (!enc.isEnabled.value || !enc.isUnlocked.value) return
    if (Date.now() - lastRunAt < MIN_INTERVAL_MS) return

    running.value = true
    lastRunAt = Date.now()
    try {
      // Two independent repairs, each in its own try: one depends on Plaid
      // being reachable and the other doesn't, so a failure in the first must
      // not park the second until the next unlock.
      try {
        const itemIds = (bankAccountsQuery.data.value || [])
          .map((it: any) => it.PlaidItemID ?? it.item_id ?? it.itemId)
          .filter(Boolean)
        // pendingOnly keeps this free when there's nothing waiting — the
        // server checks the flag before it touches Plaid.
        const result = await mergeMutation.mutateAsync({ itemIds, pendingOnly: true })
        if (result.resealed) {
          console.info(`[sealed-merge] merged ${result.resealed} row(s) after ${reason}`)
        }
      } catch (e) {
        // Silent by design — the flag survives, so the next unlock or sync
        // retries.
        console.warn('[sealed-merge] deferred:', e)
      }

      // Same idea, different cause: rows sealed under a scope they no longer
      // live in. Accounts only here — there are a handful of them and the
      // scan is one small query, whereas the expense sweep walks a ledger.
      // Expenses are swept at the moment drift is created (right after a
      // scope move) and from the manual repair in Settings.
      try {
        const repaired = await resealMutation.mutateAsync({})
        if (repaired.accounts) {
          console.info(
            `[sealed-merge] re-sealed ${repaired.accounts} account(s) after ${reason}`,
          )
        }
      } catch (e) {
        console.warn('[sealed-merge] re-seal deferred:', e)
      }
    } finally {
      running.value = false
    }
  }

  function start(): () => void {
    // Unlocking is the moment the client first becomes able to do this.
    const stop = watch(
      () => enc.isUnlocked.value,
      (unlocked) => { if (unlocked) void drain('unlock') },
      { immediate: true },
    )

    // A sync is when new upstream changes actually appear (useTransactionSync
    // fires this once the pull has committed).
    const onSynced = () => void drain('sync')
    window.addEventListener('app:transactions-synced', onSynced)

    return () => {
      stop()
      window.removeEventListener('app:transactions-synced', onSynced)
    }
  }

  return { drain, start, running }
}
